import React, { useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Download, FileText, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export const Catalogs = () => {
  const catalogs = [
    {
      id: 1,
      title: 'Dental Surgery Instruments Catalog',
      description: '30+ dental instruments including extraction forceps (Upper/Lower), elevators (Cryer, Apexo), scalers, curettes (Gracey, Universal), surgical scissors, needle holders, and diagnostic instruments',
      pages: '5 pages',
      size: '77 KB',
      image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQ4Njh8MHwxfHNlYXJjaHwxfHxkZW50YWwlMjBpbnN0cnVtZW50c3xlbnwwfHx8fDE3MzQ1Njk3NDB8MA&ixlib=rb-4.0.3&q=85',
      featured: true,
      category: 'Dental Surgery',
      pdfFile: '/catalogs/dental-surgery-catalog-2025.pdf'
    },
    {
      id: 2,
      title: 'General Surgery Instruments Catalog',
      description: '35+ surgical instruments including scalpel handles, Mayo & Metzenbaum scissors, hemostatic forceps (Kelly, Crile, Mosquito), tissue forceps (Adson, DeBakey), retractors, and needle holders',
      pages: '5 pages',
      size: '80 KB',
      image: 'https://images.unsplash.com/photo-1668453598237-27098d2575ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxzdXJnaWNhbCUyMGluc3RydW1lbnRzfGVufDB8fHx8MTc4MTUxMjczN3ww&ixlib=rb-4.1.0&q=85',
      category: 'General Surgery',
      pdfFile: '/catalogs/general-surgery-catalog-2025.pdf',
      featured: true
    },
    {
      id: 3,
      title: 'Company Brochure',
      description: 'Complete overview of Universa Surgical - dental and general surgery instrument manufacturer with ISO 13485 certification and global presence',
      pages: '1 page',
      size: '1.2 MB',
      image: 'https://images.unsplash.com/photo-1685997179880-6449203a053e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxzdXJnaWNhbCUyMGluc3RydW1lbnRzfGVufDB8fHx8MTc4MTUxMjczN3ww&ixlib=rb-4.1.0&q=85',
      category: 'Company',
      pdfFile: '/catalogs/universa-surgical-brochure-2025.pdf',
      featured: false
    }
  ];

  const handleDownload = (catalog) => {
    // Direct download of actual PDF file
    const pdfUrl = catalog.pdfFile || '/catalogs/complete-catalog-2025.pdf';
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `${catalog.title.replace(/\s+/g, '-')}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Downloading ${catalog.title}...`, {
      description: 'Your catalog download has started.'
    });
  };

  const handleDownloadAll = () => {
    // Download the complete catalog
    const link = document.createElement('a');
    link.href = '/catalogs/complete-catalog-2025.pdf';
    link.download = 'Universa-Surgical-Complete-Catalog-2025.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Downloading complete catalog...', {
      description: 'Your complete product catalog download has started.'
    });
  };

  return (
    <div className="min-h-screen pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-red-600 text-white border-0">Product Catalogs</Badge>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Download Our Catalogs</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Browse and download our comprehensive product catalogs. All catalogs are available in PDF format.
          </p>
          <Button 
            size="lg" 
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={handleDownloadAll}
          >
            <Download className="w-5 h-5 mr-2" />
            Download All Catalogs
          </Button>
        </div>

        {/* Featured Catalog */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Catalog</h2>
          {useMemo(() => catalogs.filter(c => c.featured).map(catalog => (
            <Card key={catalog.id} className="border-0 shadow-xl overflow-hidden bg-gradient-to-br from-red-50 to-white">
              <CardContent className="p-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="relative h-80 md:h-auto">
                    <img 
                      src={catalog.image} 
                      alt={catalog.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <Badge className="absolute top-4 right-4 bg-red-600 text-white border-0">
                      Featured
                    </Badge>
                  </div>
                  <div className="p-8 flex flex-col justify-center">
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">{catalog.title}</h3>
                    <p className="text-gray-700 mb-6 text-lg">{catalog.description}</p>
                    <div className="flex items-center gap-6 mb-6 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        <span>{catalog.pages}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Download className="w-4 h-4" />
                        <span>{catalog.size}</span>
                      </div>
                    </div>
                    <Button 
                      size="lg" 
                      className="bg-red-600 hover:bg-red-700 text-white w-full md:w-auto"
                      onClick={() => handleDownload(catalog)}
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Download Complete Catalog
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )), [catalogs])}
        </div>

        {/* Category Catalogs */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Category Catalogs</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useMemo(() => catalogs.filter(c => !c.featured).map(catalog => (
              <Card key={catalog.id} className="group hover:shadow-xl transition-all duration-300 border-0 overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={catalog.image} 
                    alt={catalog.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <Badge className="absolute top-3 right-3 bg-red-600 text-white border-0 text-xs">
                    {catalog.category}
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-bold text-xl text-gray-900 mb-2 group-hover:text-red-600 transition-colors">
                    {catalog.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{catalog.description}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    <div className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      <span>{catalog.pages}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Download className="w-3 h-3" />
                      <span>{catalog.size}</span>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => handleDownload(catalog)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                </CardContent>
              </Card>
            )), [catalogs])}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-16 bg-gray-50 rounded-2xl p-8 md:p-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Need Custom Catalogs?</h2>
            <p className="text-lg text-gray-600 mb-6">
              We can create customized product catalogs tailored to your specific needs and requirements. 
              Contact our team to discuss your requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white">
                  Contact Us
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/products">
                <Button size="lg" variant="outline" className="border-red-600 text-red-600 hover:bg-red-50">
                  Browse Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
